import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import axios, { AxiosError, type AxiosResponse } from 'axios'
import { nanoid } from 'nanoid'
import { isEmail, submitDataToHubspotForm } from 'shared/lib'
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from 'shared/constants/auth'
import { getEnvVar } from 'shared/config/lib/getEnvVar'
import { AuthApi } from 'entities/Auth/api'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { TwoFaModalStore } from 'entities/Auth/ui/TwoFaModal'
import { type IResponseProfile } from 'entities/Users/api/types'
import { getBaseApiParams } from 'entities/Auth/helpers/getBaseApiParams'
import { type RegisterStore } from 'pages/register/ui/Register'
import { FieldsEnum } from './type'

export class StepSignUpStore {
  private _loading = false
  private _password = ''
  private _email = ''
  private _error: null | string = null
  private _handleNextStep: null | (() => void) = null
  private _activeField: FieldsEnum | null = null

  // Email validation
  private _disposeEmailValidation: IReactionDisposer | null = null
  private _emailError: string | null = null
  private _emailValidationTrigger: string | null = null
  private _showConstantEmailError = false

  // Password validation
  private _disposePasswordValidation: IReactionDisposer | null = null
  private _passwordError: string | null = null
  private _passwordValidationTrigger: string | null = null
  private _showConstantPasswordError = false

  private readonly _twoFaModalStore: TwoFaModalStore

  constructor(private _registerStore: RegisterStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._twoFaModalStore = new TwoFaModalStore(this._entities.authStore)
    this.reactionEmailValidation()
    this.reactionPasswordValidation()
  }

  get storeReCaptchaStore() {
    return this._registerStore.storeReCaptchaStore
  }

  get twoFaModalStore() {
    return this._twoFaModalStore
  }

  get showConstantEmailError() {
    return this._showConstantEmailError
  }

  get showConstantPasswordError() {
    return this._showConstantPasswordError
  }

  get emailError() {
    return this._emailError
  }

  get hasEmail() {
    return !!this.email
  }

  get hasPassword() {
    return !!this.password
  }

  get isMaxPassword() {
    return this.password.length === MAX_PASSWORD_LENGTH
  }

  get passwordError() {
    return this._passwordError
  }

  get email() {
    return this._email
  }

  get password() {
    return this._password
  }

  get emailLength() {
    return this.email.length
  }

  get passwordLength() {
    return this.password.length
  }

  get loading() {
    return this._loading
  }

  get error() {
    return this._error
  }

  get isValidEmail() {
    return isEmail(this._email)
  }

  get isValidPassword() {
    return (
      this._password.length >= MIN_PASSWORD_LENGTH && this._password.length <= MAX_PASSWORD_LENGTH
    )
  }

  get isActiveEmailField() {
    return this._activeField === FieldsEnum.email
  }

  get isActivePasswordField() {
    return this._activeField === FieldsEnum.password
  }

  get deviceId() {
    return localStorage.getItem('device_id')
  }

  reset = () => {
    this._email = ''
    this._password = ''
    this._activeField = null
  }

  init = () => {
    const searchParams = new URLSearchParams(window.location.search)
    const email = searchParams.get('email')

    if (email) this.setEmail(email)

    this._entities.authStore.setOnGoogleSignUpSuccess(async (IResponseGoogleAuth) => {
      const {
        data: { email },
      }: AxiosResponse<IResponseProfile> = await axios.get(
        'users/profile',
        getBaseApiParams(IResponseGoogleAuth.token)
      )

      if (email) {
        submitDataToHubspotForm({
          portalId: getEnvVar('HUBSPOT_PORTAL_ID_SIGNUP_FREE_TRIAL_MIRROR'),
          formId: getEnvVar('HUBSPOT_FORM_ID_SIGNUP_FREE_TRIAL_MIRROR'),
          fields: [
            {
              name: 'email',
              value: email,
            },
          ],
          isShowErrorMessage: false,
        })
      }
    })
  }

  signUp = async () => {
    this._showConstantEmailError = true
    this._showConstantPasswordError = true
    this.triggerEmailValidation()
    this.triggerPasswordValidation()
    if (!this.isValidEmail || !this.isValidPassword) return

    try {
      this._loading = true

      await AuthApi.validateEmail({
        email: this._email,
        checkDomain: false,
      })

      await this.storeReCaptchaStore.setExecuteRecaptcha('sign_up_register')

      await this._entities.authStore.signUpRegister(
        {
          email: this._email,
          password: this._password,
          ...this.storeReCaptchaStore.params,
        },
        {
          reCaptchaStore: this.storeReCaptchaStore,
        }
      )
      this._loading = false

      submitDataToHubspotForm({
        portalId: getEnvVar('HUBSPOT_PORTAL_ID_SIGNUP_FREE_TRIAL_MIRROR'),
        formId: getEnvVar('HUBSPOT_FORM_ID_SIGNUP_FREE_TRIAL_MIRROR'),
        fields: [
          {
            name: 'email',
            value: this._email || '',
          },
        ],
        isShowErrorMessage: false,
      })

      if (this._handleNextStep) this._handleNextStep()
    } catch (e) {
      if (e instanceof AxiosError) {
        const existError = 'This email is already in use.'

        if (e.errors.getError('email') === existError) {
          this.trySignIn(existError)
          return
        }

        this.storeReCaptchaStore.checkServerError(e.errors.getError('message'))

        this._error = e.errors.getError('email') || e.errors.getError('error') || null
      }
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  trySignIn = async (error: string) => {
    try {
      this._loading = true

      await this.storeReCaptchaStore.setExecuteRecaptcha('2fa_auth_login')

      const data = await this._entities.authStore.login(
        {
          email: this.email,
          password: this.password,
          device_id: this.deviceId,
          ...this.storeReCaptchaStore.params,
        },
        {
          reCaptchaStore: this.storeReCaptchaStore,
        }
      )

      if (data) {
        this._twoFaModalStore.openTwoFaModal(data)
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (this.storeReCaptchaStore.checkServerError(e.errors.getError('message'))) return
      }

      this._error = error
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  focusEmailField = () => {
    this.setActiveField(FieldsEnum.email)
  }

  focusPasswordField = () => {
    this.setActiveField(FieldsEnum.password)
  }

  blurPasswordField = () => {
    this.blurActiveField(FieldsEnum.password)
  }

  blurEmailField = () => {
    this.blurActiveField(FieldsEnum.email)
  }

  blurActiveField = (value: FieldsEnum) => {
    if (this._activeField !== value) return
    this._activeField = null
  }

  setActiveField = (value: FieldsEnum) => {
    this._activeField = value
  }

  triggerEmailValidation = () => {
    this._emailValidationTrigger = nanoid()
  }

  triggerPasswordValidation = () => {
    this._passwordValidationTrigger = nanoid()
  }

  setEmail = (value: string) => {
    if (this._error) this._error = null
    if (this._showConstantEmailError) this._showConstantEmailError = false
    this._email = value.trim()
  }

  setPassword = (value: string) => {
    if (value.length > MAX_PASSWORD_LENGTH) return
    if (this._error) this._error = null
    if (this._showConstantPasswordError) this._showConstantPasswordError = false
    this._password = value
  }

  setHandleNextStep = (handleNextStep: () => void) => {
    this._handleNextStep = handleNextStep
  }

  reactionEmailValidation = () => {
    this._disposeEmailValidation?.()
    this._disposeEmailValidation = reaction(
      () => [this._emailValidationTrigger, this.isValidEmail],
      ([trigger, isValid]) => {
        this._emailError =
          trigger && !isValid
            ? this.hasEmail
              ? 'Please enter a valid email address.'
              : 'Please enter email address.'
            : null

        if (isValid) this._emailValidationTrigger = null
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionPasswordValidation = () => {
    this._disposePasswordValidation?.()
    this._disposePasswordValidation = reaction(
      () => [this._passwordValidationTrigger, this.isValidPassword],
      ([trigger, isValid]) => {
        this._passwordError =
          trigger && !isValid
            ? this.hasPassword
              ? `Password must contain at least ${MIN_PASSWORD_LENGTH} characters.`
              : 'Please enter password.'
            : null
        if (isValid) this._passwordValidationTrigger = null
      },
      {
        fireImmediately: true,
      }
    )
  }
}
